import moment from "moment";
import { exists } from "../utils";
import { Id_floor, Id_workorder, Id_location, Id_asset, Id_workorder_asset } from "../api/Identifiers";
import { MapFeature } from "../MapFeature";
import { Point } from "../Point";
import { Rectangle } from "../Rectangle";
import { Configuration } from "../Configuration";
import { get_state, State } from "../redux/State";
import { Style, Icon, Text, GeomPoint, Feature } from '../openlayers/ol-bridge';

interface WorkorderJSON {
    active: boolean;
    //adj_coords: number[];
    category: string;
    cmsViewIcon: string;   //  AWH remove?
    coords: number[];
    icon: string;
    icon_red: string;
    icon_yellow: string;
    is_red: boolean;
    is_yellow: boolean;
    id: string;   // id_workorder is a UUID
    id_floor: number;
    id_location: number;
    idCmsStatus: number;
    idFloor: number;   // AWH remove?
    rawCoords: number[];
    seconds_until_due: number;
    type: string;
    xCoord: number;  // AWH remove?
    yCoord: number;  // AWH remove?
    has_coords: boolean;
    is_multi_asset: boolean;
    asset_info: any[];
}

export class Workorder {
    private _active: boolean;
    private _category: string;
    private _raw_coord: Point;
    private _raw_geo_ref: Rectangle;

    private _icon: string;
    private _id: Id_workorder;
    private id_cms_status: number;
    private _id_location: Id_location;
    private _id_floor: Id_floor;
    private _raw: any;
    private _type: string;                
    // AWH TODO: this needs to be removed as it is superceeded by category.  
    //Category needs to be a numeric_id and way it's stored in the state needs to be updated.

    private _asset_info: any[];

    public get generic_id(): string { return this.id.toString(); }


    public get icon_url(): string {
        if (this.is_red) {
            return this.icon_red;
        }
        if (this.is_yellow) {
            return this.icon_yellow;
        }
        return this.icon_plain;
    }



    public get raw_coords(): Point | null { return this._raw_coord; }
    public get raw_geo_ref(): Rectangle | null { return this._raw_geo_ref; }
    public get map_feature_type(): string {
        if (this.is_multi_asset) {
            return "multi-asset-workorder";
        }
        return "workorder";
    }

    get active(): boolean { return this._active; }
    get category(): string { return this._category; }
    get id_floor(): Id_floor {
        if (!this._id_floor) {
            return Id_floor.wrap(this._id_location.value);
        }
        return this._id_floor;
    }
    get id_location(): Id_location { return this._id_location; }
    get id(): Id_workorder { return this._id; }
    get icon_plain(): string { return this._icon; }
    get is_red(): boolean { return this.seconds_until_due < 0; }
    get is_yellow(): boolean { return this.seconds_until_due < 60 * 60 * 24 * 7; }  // due in less than 7 days

    get icon_red(): string {
        return this._icon.replace("/48/", "/48_red/");
    }

    get icon_yellow(): string {
        return this._icon.replace("/48/", "/48_yellow/");
    }

    get icon_smart(): string {

        if (this.is_red) {
            return this.icon_red;
        }

        if (this.is_yellow) {
            return this.icon_yellow;
        }

        return this.icon_plain;

    }

    public get raw(): any {
        return this._raw;
    }
    public get type(): string {
        return this._type;
    }

    get date_needed_by(): moment.Moment {
        if (!_.has(this.raw, "dateNeededBy")) {
            return moment((moment().year() + 100) + "-01-01"); // if no dateNeededBy is present, set the due date to 100 years in the future
        }
        return moment(this.raw.dateNeededBy);
    }

    get seconds_until_due(): number {
        return Math.ceil((moment.duration(this.date_needed_by.diff(moment())).asSeconds()));
    }


    public static build_from_raw(raw_workorder: any): Workorder {

        if (!raw_workorder.id) {
            console.warn("Error. Workorder with missing id");
            return null;
        }

        if (!raw_workorder.assets) {
            let raw = raw_workorder.id;
            console.warn("Error. Workorder is missing assets - " + raw);
        }

        let r: Workorder = new Workorder;

        r._raw = (<any>Object).assign({}, raw_workorder);
        r._id = Id_workorder.wrap(raw_workorder.id);
        if (raw_workorder.idCmsStatus === undefined || raw_workorder.idCmsStatus === null) {
            console.warn(`malformed workorder ${r._id.toString()} has no idCmsStatus value`, raw_workorder);
            return null;
        }
        r._active = utilsWorkorderIsActive(raw_workorder.idCmsStatus);
        // if (!raw_workorder.assetCount) {
        //     r._is_multi_asset = false;
        // }
        r._asset_info = utilsWorkorderMultiAssetInfo(raw_workorder.assets);
        r.id_cms_status = raw_workorder.idCmsStatus;
        r._id_floor = Id_floor.wrap(raw_workorder.idFloor);
        if (!r._id_floor) {
            console.warn(`malformed workorder ${r.id.toString()} has no idFloor value`);
            return null;
        }

        r._id_location = Id_location.wrap(raw_workorder.idLocation);
        if (!r._id_location) {
            console.warn(`malformed workorder ${r.id.toString()} has no idLocation value`);
            return null;
        }

        r._category = raw_workorder.category;
        if (!r.category) {
            console.warn(`malformed workorder ${r.id.toString()} has no category value`);
            return null;
        }
        r._type = utilsWorkorderType(r.category, raw_workorder.idCmsStatus);
        if (!raw_workorder.cmsViewIcon) {
            console.warn(`malformed workorder ${r.id.toString()} has no cmsViewIcon value`);
            return null;
        }
        r._icon = utilsWorkorderIcon(raw_workorder.cmsViewIcon);
        r._raw_coord = Point.build(raw_workorder.xCoord, raw_workorder.yCoord);
        r._raw_geo_ref = Rectangle.build_from_numbers(raw_workorder.p1xCoord, 
            raw_workorder.p1yCoord, raw_workorder.p2xCoord, raw_workorder.p2yCoord);
        return r;
    }

    public get has_coords(): boolean { return exists(this._raw_coord); }

    public get is_multi_asset(): boolean { return this.asset_info.length > 1; }

    public get asset_info(): any[] { return this._asset_info; }


    public get toJSON(): WorkorderJSON {
        return this.as_json;
    }

    public get as_json(): WorkorderJSON {

        let coords_array: number[];
        let raw_geo_ref_json: any[];
        if (!exists(this._raw_coord)) {
            coords_array = Point.NULL.as_array;
        }
        else {
            coords_array = this.raw_coords.as_array;
        }
        if (!exists(this._raw_geo_ref)) {
            raw_geo_ref_json = Rectangle.NULL.as_array;
        }
        else {
            raw_geo_ref_json = this._raw_geo_ref.as_array;
        }

        const r: WorkorderJSON = {
            active: this.active,
            //adj_coords: this.adj_coords.as_array,
            coords: coords_array,
            category: this.category,
            cmsViewIcon: this.icon_plain,
            icon: this.icon_plain,
            icon_red: this.icon_red,
            icon_yellow: this.icon_yellow,
            id: this.id.value,
            id_location: this.id_floor.value,
            id_floor: this.id_floor.value,
            idCmsStatus: this.id_cms_status,
            idFloor: this.id_floor.value,
            is_red: this.is_red,
            is_yellow: this.is_yellow,
            rawCoords: coords_array,
            seconds_until_due: this.seconds_until_due,
            type: this.type,
            xCoord: coords_array[0],
            yCoord: coords_array[1],
            has_coords: this.has_coords,
            is_multi_asset: this.is_multi_asset,
            asset_info: this._asset_info
        };

        return r;
    }

    public get as_map_feature_array(): MapFeature[] {
        let retval: WorkorderMapFeature[] = [];
        let state: State = get_state();
        let icon: string = this.icon_smart;
        if (this.is_multi_asset) {
            this.asset_info.forEach((val: any) => {
                if (val.hasCoords === false) {
                    return;
                }
                const workorderCompletedAndGroupedWorkorderActive = state.procedureReadings.assetProcedureReadingCompletionLookup.get(
                    val.id_workorder_asset) === true && this.active;
                if (!Configuration.showCheckboxOnWorkOrderCompletion && workorderCompletedAndGroupedWorkorderActive) {
                    return;
                }

                let raw_coords = Point.build_from_array(val.coord);
                let raw_geo_ref = Rectangle.build_from_array(val.geo_ref);

                if (Configuration.workorder_use_asset_icons && 
					!(state.menu_asset_categories_selected.length > 0) && val.cmsview_icon_asset !== undefined) {
                    icon = val.cmsview_icon_asset;
                }

                let wo = WorkorderMapFeature.build(raw_coords, raw_geo_ref, icon, val.id_workorder, val.id_asset, true, 
                    val.id_workorder_asset, workorderCompletedAndGroupedWorkorderActive);

                retval.push(wo);
            });

            return retval;
        }

        let id_asset = null;
        if (Configuration.workorder_use_asset_icons && !(state.menu_asset_categories_selected.length > 0)) {
            if (this.asset_info.length > 0 && this.asset_info[0].cmsview_icon_asset !== undefined) {
                icon = this.asset_info[0].cmsview_icon_asset;
                id_asset = this.asset_info[0].id_asset;
            }
        }

        return [WorkorderMapFeature.build(this.raw_coords, this.raw_geo_ref, icon, this.id, id_asset, false, null, false)];
    }
}

export class WorkorderMapFeature extends MapFeature {


    private _raw_coord: Point;
    private _raw_geo_ref: Rectangle;

    private _icon: string;
    private _id_unique: string;
    private _id_workorder: Id_workorder;
    private _id_asset: Id_asset;
    private _is_asset_workorder: boolean;
    private _id_workorder_asset: Id_workorder_asset;
    private isActiveMultiAssetWorkorder: boolean;

    public get raw(): any { return this.as_json; }
    public get generic_id(): string { return this._id_unique; }
    public get icon_url(): string { return this._icon; };


    public get map_feature_type(): string {
        if (this._is_asset_workorder) {
            return "multi-asset-workorder";
        }
        return "workorder";
    }

    public get raw_coords(): Point | null { return this._raw_coord; }
    public get raw_geo_ref(): Rectangle | null { return this._raw_geo_ref; }


    public static build(
        raw_coords: Point,
        raw_geo_ref: Rectangle,
        icon_url: string,
        id_workorder: Id_workorder,
        id_asset: Id_asset,
        is_asset_workorder: boolean,
        id_workorder_asset: Id_workorder_asset,
        isActiveMultiAssetWorkorder: boolean) {
        let retval: WorkorderMapFeature = new WorkorderMapFeature();
        let id_unique: string = id_workorder.value;
        if (is_asset_workorder) {
            id_unique = id_workorder_asset.value;
        }

        retval._raw_coord = raw_coords;
        retval._icon = icon_url;
        retval._id_workorder = id_workorder;
        retval._id_asset = id_asset;
        retval._id_unique = id_unique;
        retval._raw_geo_ref = raw_geo_ref;
        retval._is_asset_workorder = is_asset_workorder;
        retval._id_workorder_asset = id_workorder_asset;
        retval.isActiveMultiAssetWorkorder = isActiveMultiAssetWorkorder;

        return retval;
    }


    public get as_json(): any {

        let coords_array: number[];
        let raw_geo_ref_json: any[];
        if (!exists(this._raw_coord)) {
            coords_array = Point.NULL.as_array;
        }
        else {
            coords_array = this.raw_coords.as_array;
        }
        if (!exists(this._raw_geo_ref)) {
            raw_geo_ref_json = Rectangle.NULL.as_array;
        }
        else {
            raw_geo_ref_json = this._raw_geo_ref.as_array;
        }

        return {
            active: true,
            adj_coords: this.adj_coords.as_array,
            id_workorder: this._id_workorder,
            id_asset: this._id_asset,
            coords: coords_array,
            category: "undefined",
            cmsViewIcon: this.icon_url,
            icon: this.icon_url,
            icon_red: this.icon_url,
            icon_yellow: this.icon_url,
            id: this._id_unique,
            is_red: false,
            is_yellow: false,
            rawCoords: coords_array,
            seconds_until_due: 100000,
            type: this.map_feature_type,
            xCoord: coords_array[0],
            yCoord: coords_array[1],
            has_coords: true,
            is_multi_asset: this._is_asset_workorder,
            id_workorder_asset: this._id_workorder_asset,
            isActiveMultiAssetWorkorder: this.isActiveMultiAssetWorkorder
        };

    }

    public createOpenlayersFeature(p0: Point): any {
        let iconStyle = [
            new Style(
                {
                    image: new Icon(({
                        anchor: [.5, .5],
                        anchorXUnits: "fraction",
                        anchorYUnits: "fraction",
                        src: this.icon_url,
                    })),
                })
        ];

        if (this.map_feature_type === "multi-asset-workorder" && this.isActiveMultiAssetWorkorder === true) {
            if (Configuration.showCheckboxOnWorkOrderCompletion) {
                iconStyle.push(
                    new Style({
                        text: new Text({
                            text: "✅",
                            font: "25px",
                            scale: 1.5,
                            offsetY: 22,
                        }),
                    })
                );
            }
        }

        let coords: Point = this.adj_cluster_coords;
        if (!exists(coords)) {
            coords = p0;
        }

        let point = new GeomPoint([coords.X, coords.Y], "XY");
        let feature = new Feature({
            geometry: point,
        });

        feature.setId(this.generic_id);
        feature.setStyle(iconStyle);
        feature.data = this.as_json;
        feature.data.feature = this.map_feature_type;
        feature.raw = this.raw;

        return feature;
    }

}

export function utilsWorkorderIsActive(id_cms_status: number): boolean {

    /**
     IDCMSStatus   IDCMSModule  CMSStatus    Description
     1             2            Pending      New work order that requires review/or attention
     2             2            Active       Work order that has been assigned or is in progress
     4             2            Cancelled    Work order that was not completed (duplicate, not finished prior to next scheduled wo, etc)
     5             2            Complete     Work order that has been completed
     */

    if (id_cms_status == 1) // Pending
    {
        return true;
    }
    if (id_cms_status == 2 || id_cms_status == 6) // Active
    {
        return true;
    }
    if (id_cms_status == 4) // Cancelled
    {
        return false;
    }
    if (id_cms_status == 5) // Complete
    {
        return false;
    }
    console.error("Unrecognized id_cms_status: " + id_cms_status);
    return false;
}

export function utilsWorkorderMultiAssetInfo(assets: any[]): any[] {
    let asset_info: any[] = [];
    for (let i = 0; i < assets.length; i++) {
        let id_asset = assets[i].id;
        let id_workorder = assets[i].workOrderId;
        let id_workorder_asset = assets[i].workOrderAssetId;
        let cmsViewIcon = assets[i].cmsViewIcon;
        let idFloor = assets[i].floorId;
        let hasCoords = assets[i].p1xCoord != null && assets[i].p1yCoord != null && assets[i].p2xCoord != null && assets[i].p2yCoord != null;
        let raw_coords: Point = Point.build(assets[i].xCoord, assets[i].yCoord);
        if (!raw_coords) {
            raw_coords = Point.build(0, 0);
        }
        let raw_geo_ref: Rectangle = hasCoords
            ? Rectangle.build_from_numbers(assets[i].p1xCoord, assets[i].p1yCoord, assets[i].p2xCoord, assets[i].p2yCoord)
            : Rectangle.build_from_numbers(0, 0, 0, 0);
        if (!raw_geo_ref) {
            raw_geo_ref = Rectangle.build_from_numbers(0, 0, 0, 0);
        }
        asset_info.push({
            "coord": raw_coords.toArray(),
            "geo_ref": raw_geo_ref.toArray(),
            "id_asset": id_asset,
            "id_workorder": id_workorder,
            "id_workorder_asset": id_workorder_asset,
            "cmsview_icon_asset": cmsViewIcon,
            "idFloor": idFloor,
            "hasCoords": hasCoords
        });

    }
    return asset_info;
}

export function utilsWorkorderType(category: string, id_cms_status: number): string {
    let category_reduced: string = category.toLowerCase().trim().replace(" ", "");

    let retVal: string = category_reduced + "_";

    if (utilsWorkorderIsActive(id_cms_status)) {
        retVal += "active";
    }
    else {
        retVal += "complete";
    }
    return retVal;
}


export function utilsWorkorderIcon(cms_view_icon: string): string {
    if (cms_view_icon === undefined) {
        console.warn("null icon encountered.");
        cms_view_icon = "";
    }
    let icon: string = cms_view_icon;

    if (icon.toLowerCase().substring(0, 4) !== "http") {
        console.warn("icon is not a complete URL");
        icon = "http://cmsview.azurewebsites.net/data/" + icon;
    }

    return icon;

}

/**
 * This takes in a raw_workorders object from the api, and returns a cleaned up list
 * of workorders
 * @param raw_workorders
 * @private
 */
export function cleanup_workorders_data(raw_workorder: any): any {  
    // This is an ECT (Extract, clean, transform) function - move it to a stand alone ECT js file
    let wo: Workorder = Workorder.build_from_raw(raw_workorder);
    let r: any = null;
    if (wo !== null) {
        r = wo.toJSON;
    }
    return r;
}


